import React, { useState } from "react";
import { Box, Typography, Paper, Button, IconButton, TextField, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoIcon from "@mui/icons-material/Info";
import { Template } from "../../types";

interface CreatePageModalProps {
  templates: Template[];
  onClose: () => void;
  onCreate: (name: string, slug: string, templateId?: string) => void;
}

// Template category icons and colors
const categoryStyles: Record<string, { icon: string; color: string; gradient: string }> = {
  home: { icon: "🏠", color: "#3b82f6", gradient: "linear-gradient(135deg, #3b82f6, #2563eb)" },
  about: { icon: "📖", color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)" },
  services: { icon: "💼", color: "#10b981", gradient: "linear-gradient(135deg, #10b981, #059669)" },
  doctors: { icon: "👨‍⚕️", color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b, #d97706)" },
  contact: { icon: "📞", color: "#ef4444", gradient: "linear-gradient(135deg, #ef4444, #dc2626)" },
  blog: { icon: "📰", color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899, #db2777)" },
  landing: { icon: "🚀", color: "#f59e0b", gradient: "linear-gradient(135deg, #F2819F, #FAAF40)" },
  custom: { icon: "✨", color: "#6366f1", gradient: "linear-gradient(135deg, #6366f1, #4f46e5)" },
};

export const CreatePageModal = ({ templates, onClose, onCreate }: CreatePageModalProps) => {
  const [step, setStep] = useState<"template" | "details">("template");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [pageName, setPageName] = useState("");
  const [pageSlug, setPageSlug] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setPageName(name);
    setPageSlug(
      name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
    );
  };

  const handleCreate = async () => {
    if (!pageName.trim()) return;
    setIsCreating(true);
    await onCreate(pageName, pageSlug || pageName.toLowerCase().replace(/\s+/g, "-"), selectedTemplate?.id);
    setIsCreating(false);
  };

  const handleSelectTemplate = (template: Template | null) => {
    setSelectedTemplate(template);
    setStep("details");
    // Pre-fill name based on template
    if (template) {
      const baseName = template.name.replace(" Page", "").replace(" Template", "");
      handleNameChange(`My ${baseName}`);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Modal */}
      <Paper
        elevation={24}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 896,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 4,
          animation: "fadeIn 0.2s ease-out",
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "scale(0.95)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            background: "linear-gradient(to right, #f8fafc, #ffffff)",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {step === "template" ? "Choose a Template" : "Page Details"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {step === "template" ? "Start with a template or create a blank page" : "Give your page a name and URL"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {step === "template" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Blank Page Option */}
              <Box>
                <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 1 }}>
                  Start Fresh
                </Typography>
                <Box
                  component="button"
                  onClick={() => handleSelectTemplate(null)}
                  sx={{
                    width: "100%",
                    mt: 1.5,
                    p: 3,
                    border: "2px dashed",
                    borderColor: "grey.300",
                    borderRadius: 3,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "primary.50",
                    },
                    "&:hover .icon-container": {
                      bgcolor: "primary.100",
                    },
                    "&:hover .icon-svg": {
                      color: "primary.main",
                    },
                    "&:hover .blank-title": {
                      color: "primary.main",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      className="icon-container"
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: "grey.100",
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s",
                      }}
                    >
                      <AddIcon
                        className="icon-svg"
                        sx={{ fontSize: 32, color: "grey.400", transition: "color 0.2s" }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        className="blank-title"
                        fontWeight={600}
                        color="text.primary"
                        sx={{ transition: "color 0.2s" }}
                      >
                        Blank Page
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start with an empty canvas
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Template Grid */}
              {templates.length > 0 && (
                <Box>
                  <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 1 }}>
                    Templates ({templates.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                      gap: 2,
                      mt: 1.5,
                    }}
                  >
                    {templates.filter((t) => !t.hidden).map((template) => {
                      const style = categoryStyles[template.category] || categoryStyles.custom;
                      return (
                        <Box
                          component="button"
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          sx={{
                            position: "relative",
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: 3,
                            overflow: "hidden",
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: 6,
                            },
                            "&:hover .template-overlay": {
                              bgcolor: "rgba(37, 99, 235, 0.05)",
                              opacity: 1,
                            },
                            "&:hover .use-button": {
                              transform: "translateY(0)",
                            },
                            "&:hover .template-name": {
                              color: "primary.main",
                            },
                          }}
                        >
                          {/* Preview Area */}
                          <Box
                            sx={{
                              height: 128,
                              background: style.gradient,
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {/* Mini preview mockup */}
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 8,
                                bgcolor: "rgba(255, 255, 255, 0.2)",
                                borderRadius: 2,
                                backdropFilter: "blur(4px)",
                              }}
                            >
                              <Box
                                sx={{
                                  height: 12,
                                  bgcolor: "rgba(255, 255, 255, 0.4)",
                                  borderTopLeftRadius: 8,
                                  borderTopRightRadius: 8,
                                }}
                              />
                              <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Box sx={{ height: 24, bgcolor: "rgba(255, 255, 255, 0.3)", borderRadius: 1 }} />
                                <Box
                                  sx={{ height: 8, bgcolor: "rgba(255, 255, 255, 0.2)", borderRadius: 1, width: "75%" }}
                                />
                                <Box
                                  sx={{ height: 8, bgcolor: "rgba(255, 255, 255, 0.2)", borderRadius: 1, width: "50%" }}
                                />
                              </Box>
                            </Box>
                            {/* Category icon */}
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                fontSize: "1.5rem",
                                opacity: 0.5,
                              }}
                            >
                              {style.icon}
                            </Box>
                          </Box>

                          {/* Info */}
                          <Box sx={{ p: 2 }}>
                            <Typography
                              className="template-name"
                              fontWeight={600}
                              color="text.primary"
                              sx={{ transition: "color 0.2s" }}
                            >
                              {template.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {template.description}
                            </Typography>
                          </Box>

                          {/* Hover overlay */}
                          <Box
                            className="template-overlay"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              bgcolor: "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "all 0.2s",
                            }}
                          >
                            <Box
                              className="use-button"
                              sx={{
                                bgcolor: "primary.main",
                                color: "white",
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                fontWeight: 500,
                                boxShadow: 4,
                                transform: "translateY(8px)",
                                transition: "transform 0.2s",
                              }}
                            >
                              Use This Template
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ maxWidth: 448, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Selected Template Preview */}
              {selectedTemplate && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: categoryStyles[selectedTemplate.category]?.gradient || categoryStyles.custom.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.5rem",
                    }}
                  >
                    {categoryStyles[selectedTemplate.category]?.icon || "✨"}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={500} color="text.primary">
                      Using: {selectedTemplate.name}
                    </Typography>
                    <Typography
                      component="button"
                      onClick={() => setStep("template")}
                      sx={{
                        fontSize: "0.875rem",
                        color: "primary.main",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Change template
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Page Name */}
              <Box>
                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                  Page Name *
                </Typography>
                <TextField
                  fullWidth
                  value={pageName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., About Us, Services, Contact"
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: "1.125rem",
                    },
                  }}
                />
              </Box>

              {/* Page URL */}
              <Box>
                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                  Page URL
                </Typography>
                <Box sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: "grey.100",
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRight: 0,
                      borderTopLeftRadius: 12,
                      borderBottomLeftRadius: 12,
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    yoursite.com/
                  </Box>
                  <TextField
                    fullWidth
                    value={pageSlug}
                    onChange={(e) => setPageSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="page-url"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  This will be the URL path for your page
                </Typography>
              </Box>

              {/* Tips */}
              <Box
                sx={{
                  bgcolor: "primary.50",
                  borderRadius: 3,
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <InfoIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "primary.dark" }}>
                    <strong>Tip:</strong> You can edit everything after creating the page. The template is just a
                    starting point!
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Button
            onClick={step === "details" ? () => setStep("template") : onClose}
            startIcon={step === "details" ? <ArrowBackIcon /> : undefined}
            sx={{ color: "text.secondary" }}
          >
            {step === "details" ? "Back" : "Cancel"}
          </Button>

          {step === "details" && (
            <Button
              onClick={handleCreate}
              disabled={!pageName.trim() || isCreating}
              variant="contained"
              endIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.25,
                borderRadius: 2,
              }}
            >
              {isCreating ? "Creating..." : "Create Page"}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
